import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MeService } from 'app/core/services/me.service';
import { ClientsService } from 'app/core/services/clients.service';
import { AuthenticationService } from 'app/core/services/authentication.service';
import { ClientConfigurationOptionsService } from 'app/core/services/client-configuration-options.service';
import { ClientConfigurationApprovalsService } from 'app/core/services/client-configuration-approvals.service';
import { Client } from 'app/core/models/client.model';
import { MinimalClient } from 'app/core/models/minimal-client.model';
import { ClientConfigurationOption } from 'app/core/models/client-configuration-option.model';
import { ClientConfigurationApproval } from 'app/core/models/client-configuration-approval.model';
import { ClientConfigurationOptionKey } from 'app/core/enums/client-configuration-option-key.enum';
import { Observable, Subscription, of, iif } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { ClientInfoCardComponent } from '../../components/client-info-card/client-info-card.component';
import { ClientConfigurationApprovalButtonComponent } from '../../components/client-configuration-approval-button/client-configuration-approval-button.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'app-client-portal-referral-program-protocol',
  templateUrl: './client-portal-referral-program-protocol.component.html',
  styleUrls: ['./client-portal-referral-program-protocol.component.scss'],
  imports: [ClientInfoCardComponent, ClientConfigurationApprovalButtonComponent, MatProgressSpinner, MatIcon]
})
export class ClientPortalReferralProgramProtocolComponent implements OnInit, OnDestroy {
  private clientsService = inject(ClientsService);
  private meService = inject(MeService);
  private clientConfigurationOptionsService = inject(ClientConfigurationOptionsService);
  private clientConfigurationApprovalsService = inject(ClientConfigurationApprovalsService);
  private authService = inject(AuthenticationService);

  loading = true;
  client: Client;
  clientConfigurationOptions: ClientConfigurationOption[];
  approval: ClientConfigurationApproval | null = null;
  private selectedClientChangedSubscription: Subscription;

  readonly Keys = ClientConfigurationOptionKey;

  ngOnInit() {
    this.loadEverything();
    this.selectedClientChangedSubscription = this.meService.selectedClientChanged
      .subscribe(() => this.loadEverything());
  }

  ngOnDestroy() {
    this.selectedClientChangedSubscription?.unsubscribe();
  }

  onApprove() {
    if (!this.approval || !this.client) { return; }
    this.clientConfigurationApprovalsService.approve(this.approval.id, this.client.id).subscribe(result => {
      this.approval = result.clientConfigurationApproval;
    });
  }

  get patientOwnershipMonths(): number {
    return this.client.patientOwnershipTimeframeInMonths;
  }

  get specialistEncounterMonths(): number {
    return this.client.specialistEncounterTimeframeInMonths;
  }

  get patientOwnershipDateText(): string {
    return this.configEnabled(ClientConfigurationOptionKey.usesWrittenDateToEstablishPatientCareOwnership) ? 'written' : 'service';
  }

  get patientOwnershipBeforeText(): string {
    return this.configEnabled(ClientConfigurationOptionKey.verifyingPatientInteractionMustBeBeforePatientCareOwnershipDate) ? 'before' : 'of';
  }

  get patientCareOwnershipOnly(): boolean {
    return this.configEnabled(ClientConfigurationOptionKey.referralNotRequiredToVerify) &&
      this.configEnabled(ClientConfigurationOptionKey.patientAttachmentNotRequiredToVerify);
  }

  get documentationRequired(): string {
    const referralNotRequired = this.configEnabled(ClientConfigurationOptionKey.referralNotRequiredToVerify);
    const attachmentNotRequired = this.configEnabled(ClientConfigurationOptionKey.patientAttachmentNotRequiredToVerify);
    const referralCanReplaceAttachment = this.configEnabled(ClientConfigurationOptionKey.referralCanReplaceConsultNote);
    if (referralNotRequired && !attachmentNotRequired && referralCanReplaceAttachment) { return 'either'; }
    if (!referralNotRequired && attachmentNotRequired) { return 'referral-only'; }
    if (referralNotRequired && !attachmentNotRequired) { return 'consult-only'; }
    if (this.patientCareOwnershipOnly) { return 'neither'; }
    return 'both';
  }

  configEnabled(key: ClientConfigurationOptionKey): boolean {
    const v = this.configValue(key);
    return v !== null && v !== undefined && v !== false && v !== 'false';
  }

  configValue(key: ClientConfigurationOptionKey): any {
    return this.clientConfigurationOptions?.find(o => o.option === key)?.value ?? null;
  }

  private loadEverything() {
    iif(
      this.isViewingAsClient(this.authService.viewingAsClient),
      this.loadClientAndOptions(this.authService.viewingAsClient),
      this.reloadUserAndOptions()
    ).pipe(
      catchError(error => {
        console.error('Error:', error);
        return of(null);
      }),
      finalize(() => this.loading = false)
    ).subscribe(data => {
      if (data) {
        this.client = data.client;
        this.clientConfigurationOptions = data.clientConfigurationOptions;
        this.loadApproval(this.client.id);
      }
    });
  }

  private isViewingAsClient(client: Client) {
    return () => client !== null;
  }

  private loadClientAndOptions(minimalClient: MinimalClient): Observable<any> {
    return of(minimalClient).pipe(
      switchMap(client => this.loadClient(client.id)),
      switchMap(client => this.loadConfigurationOptions(client))
    );
  }

  private reloadUserAndOptions(): Observable<any> {
    return this.loadUser().pipe(
      switchMap(user => {
        const clientId = user.defaultAllowedClientId || user.availableClients[0].id;
        return this.loadClient(clientId);
      }),
      switchMap(client => this.loadConfigurationOptions(client))
    );
  }

  private loadUser() {
    return this.meService.get();
  }

  private loadClient(clientId: number) {
    return this.clientsService.get(clientId);
  }

  private loadConfigurationOptions(client: Client): Observable<{
    client: Client; clientConfigurationOptions: ClientConfigurationOption[]
  }> {
    return this.clientConfigurationOptionsService.getActiveList(client.id).pipe(
      map(result => ({ client, clientConfigurationOptions: result.clientConfigurationOptions }))
    );
  }

  private loadApproval(clientId: number) {
    this.clientConfigurationApprovalsService.getLatest(clientId).subscribe(result => {
      this.approval = result.clientConfigurationApproval;
    });
  }
}
